/**
 * IMPORTS
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Trash, Undo2 } from "lucide-react";

// components
import { Button } from "@components/button/button";
import { TutorialVideoModal } from "@components/tutorial-video-modal";

// components / pages
import { ModalConfirmacaoRemessa } from "@pages/client/details-model/components/modal-confirmacao";
import { TermModal } from "@pages/client/details-model/components/modal-envio-excel";
import { useDetailsModel } from "@pages/client/details-model/view-model";

// hooks / video tutorial
import { useBuscarVideoTutorial } from "@/hooks/videos-tutorias/buscar-video-tutorial";

// store-zustand
import { useStoreZustandModelosCliente } from "@/store-zustand/cliente/details-modelo";

const DetailsModelPage = () => {
  const navigate = useNavigate();
  const [isTutorialModalOpen, setIsTutorialModalOpen] = useState(false);
  const { clearPersistedData, isModalSolicitacaoRemessa, setisModalSolicitacaoRemessa } =
    useStoreZustandModelosCliente();

  const {
    videoUrl,
    videoNome,
    isLoading: isLoadingVideoTutorial,
    isError: isErrorVideoTutorial,
  } = useBuscarVideoTutorial(isTutorialModalOpen);

  const {
    isProcessing,
    totalFiles,
    processingLog,
    obligatory,
    dadosRemessa,
    setIsOpenModal,
    campos_variaveis,
    handleDownloadTemplate,
    handleFileUpload,
    handleImageUpload,
    processedCount,
    handleDeleteRow,
    handleSubmitFile,
    isOpenModal,
    handleSolicitarRemessa,
    modeloUnico,
    handleConfirmarSolicitacao,
    isLoadingSolicitarRemessa,
    setDadosRemessa,
    handleFiltrarMatricula,
    filtroMatricula,
    setFiltroMatricula,
    setQtdModelosProvisirio,
    handleCleanState,
  } = useDetailsModel();

  return (
    <div className="min-h-screen bg-gray-100 p-4 text-sm text-gray-800">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="mb-4 text-xl font-semibold">Modelo: {modeloUnico?.nome_modelo}</h1>
          <p className="mb-2 text-red-600">
            OBS: o Limite máximo de solicitações por remessa é de 50 unidades. Para mais
            quantidades, repita o processo.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="default"
            className="h-8 gap-1 text-neutral-50"
            onClick={() => {
              handleCleanState();
              clearPersistedData();
              navigate("/");
            }}
          >
            <Undo2 className="h-3.5 w-3.5" />
            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">Voltar</span>
          </Button>

          <Button
            size="sm"
            variant="destructive"
            className="h-8 gap-1 text-neutral-50"
            onClick={() => {
              handleCleanState();
              window.location.reload();
            }}
          >
            <Trash className="h-3.5 w-3.5" />
            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">Limpar dados</span>
          </Button>
        </div>
      </div>

      {/* ETAPAS */}
      <div className="mb-6 flex flex-col gap-3">
        {Number(modeloUnico?.modeloCompleto?.tem_dados_variaveis) === 1 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-medium">Antes de enviar a planilha</p>
            <p className="mt-1 text-amber-800">
              Use apenas <strong>uma aba</strong> no arquivo Excel. Se você criou a aba
              &quot;Dados&quot;, exclua abas vazias como &quot;Planilha1&quot; (clique com o botão
              direito na aba → Excluir). Abas extras permanecem no arquivo enviado à produção.
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {Number(modeloUnico?.modeloCompleto?.tem_dados_variaveis) === 1 && (
            <>
              <button
                className="rounded bg-blue-600 px-4 py-2 text-white"
                onClick={handleDownloadTemplate}
              >
                1) Baixar Planilha
              </button>
              <div className="flex items-center gap-2">
                <button
                  className="rounded bg-indigo-500 px-4 py-2 text-white"
                  onClick={() => document.getElementById("fileInput")?.click()}
                >
                  2) Enviar Planilha
                </button>

                <input
                  id="fileInput"
                  type="file"
                  accept=".xlsx, .xls"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </div>
              {modeloUnico?.modeloCompleto?.tem_carga_foto ? (
                <div className="flex items-center gap-2">
                  <button
                    className="rounded bg-blue-400 px-4 py-2 text-white"
                    onClick={() => document.getElementById("imageInput")?.click()}
                  >
                    3) Enviar fotos
                  </button>
                  <input
                    id="imageInput"
                    type="file"
                    accept=".zip,application/zip,application/x-zip-compressed"
                    multiple
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files)}
                    disabled={isProcessing}
                  />
                </div>
              ) : (
                <></>
              )}
            </>
          )}
          <button
            className="rounded bg-green-500 px-4 py-2 text-white"
            onClick={handleConfirmarSolicitacao}
          >
            {`${Number(modeloUnico?.modeloCompleto?.tem_dados_variaveis) === 1 ? "4" : "1"}) Solicitar Impressão da remessa`}
          </button>

          <button
            className="rounded bg-primary px-4 py-2 text-white"
            onClick={() => navigate("/request-shipment-loose")}
          >
            Solicitar avulsa
          </button>

          <button
            type="button"
            className="whitespace-nowrap rounded bg-amber-600 px-4 py-2 text-white hover:bg-amber-700"
            onClick={() => setIsTutorialModalOpen(true)}
          >
            Tutorial de Solicitação
          </button>
        </div>
      </div>

      {/* Status de Processamento */}
      {isProcessing && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="mb-2 flex items-center">
            <div className="mr-3 h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
            <span className="font-medium">Processando imagens e arquivos ZIP...</span>
          </div>
          {totalFiles > 0 && (
            <div className="text-sm text-gray-600">Encontradas {totalFiles} imagens</div>
          )}
        </div>
      )}

      {/* Resultado */}
      {processedCount > 0 && !isProcessing && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="mb-2 flex items-center">
            <span className="mr-2 text-green-600">✅</span>
            <span className="font-medium">
              {processedCount} de {totalFiles} imagens processadas com sucesso!
            </span>
          </div>
          {processedCount < totalFiles && (
            <div className="text-sm text-orange-600">
              ⚠️ {totalFiles - processedCount} imagens não foram associadas (sem correspondência)
            </div>
          )}
        </div>
      )}

      {/* Log de Arquivos */}
      {processingLog?.length > 0 && (
        <details className="mb-4">
          <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
            📋 Ver arquivos encontrados ({processingLog.length})
          </summary>
          <div className="mt-2 max-h-40 overflow-y-auto rounded border bg-gray-50 p-3 font-mono text-xs">
            {processingLog.map((log, index) => (
              <div key={index} className="py-1">
                {log}
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="grid grid-cols-1 gap-6">
        {modeloUnico?.modeloCompleto?.tem_dados_variaveis ? (
          <div className="rounded bg-white p-4 shadow">
            <h2 className="mb-2 font-semibold">Consulta de Dados</h2>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <select className="rounded border border-gray-300 p-2">
                <option>{obligatory}</option>
              </select>
              <input
                type="text"
                className="flex-1 rounded border border-gray-300 p-2"
                value={filtroMatricula}
                onChange={(e) => setFiltroMatricula(e.target.value)}
              />
              <button
                className="rounded bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
                onClick={handleFiltrarMatricula}
              >
                Filtrar
              </button>
            </div>

            <p className="mb-2 text-xs text-gray-500">
              Essa ficha técnica possui apenas o campo chave!
            </p>

            <div className="overflow-x-auto rounded border border-gray-200">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-bg-table-row uppercase tracking-wide text-gray-700">
                  <tr>
                    <th className="p-3">
                      <input
                        type="checkbox"
                        className="accent-blue-500"
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const updated = dadosRemessa?.map((row) => ({
                            ...row,
                            selected: checked,
                          }));
                          // Atualiza o estado global ou local
                          setDadosRemessa(updated!);
                        }}
                        checked={
                          //@ts-expect-error
                          dadosRemessa?.length > 0 && dadosRemessa?.every((row) => row.selected)
                        }
                      />
                    </th>
                    <th className="p-3">
                      {obligatory.charAt(0).toUpperCase() + obligatory.slice(1)}
                    </th>
                    {!!modeloUnico.modeloCompleto.tem_carga_foto && <th className="p-3">Imagem</th>}
                    {campos_variaveis.map((campo, index) => (
                      <th key={index} className="p-3">
                        {campo.charAt(0).toUpperCase() + campo.slice(1)}
                      </th>
                    ))}

                    <th className="p-3">Ações</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {/**@ts-ignore */}
                  {dadosRemessa?.length > 0 ? (
                    dadosRemessa!.map((row, index) => (
                      <tr key={index} className="transition-colors hover:bg-green-200">
                        <td className="p-3">
                          <input
                            type="checkbox"
                            className="accent-blue-500"
                            checked={(row.selected as any) || false}
                            onChange={(e) => {
                              const updated = [...dadosRemessa!];
                              updated[index].selected = e.target.checked;
                              setDadosRemessa(updated);
                            }}
                          />
                        </td>
                        <td className="p-3">{String(row[obligatory])}</td>
                        {!!modeloUnico.modeloCompleto?.tem_carga_foto && (
                          <td className="p-3">
                            {row.imageUrl ? (
                              <img
                                src={row.imageUrl}
                                alt={row.foto}
                                className="h-12 w-12 cursor-pointer rounded border object-cover transition hover:shadow-md"
                                onClick={() => window.open(row.imageUrl, "_blank")}
                                title="Clique para ampliar"
                              />
                            ) : (
                              <div className="flex h-12 w-12 items-center justify-center rounded border border-gray-300 bg-gray-100 text-xl text-gray-400">
                                📷
                              </div>
                            )}
                          </td>
                        )}
                        {campos_variaveis?.map((campo, campoIndex) => (
                          <td key={campoIndex} className="p-3">
                            {String(row[campo] || "-")}
                          </td>
                        ))}

                        <td className="p-3">
                          <button
                            onClick={() => handleDeleteRow(index)}
                            className="rounded bg-red-500 px-3 py-1 text-xs text-white transition hover:bg-red-600"
                          >
                            Deletar
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="p-3 text-center text-gray-400">
                        Nenhum dado encontrado
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="w-full rounded p-4 shadow">
              <Button
                size="default"
                variant="default"
                className="h-8 w-full gap-1 text-neutral-50"
                onClick={handleConfirmarSolicitacao}
              >
                <span className="sm:whitespace-nowra sr-only text-neutral-50 sm:not-sr-only">
                  Solicitar Impressão da remessa
                </span>
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded bg-white p-4 shadow">
            <h3 className="mb-2 text-lg font-semibold text-gray-800">Solicitação de Impressão</h3>

            <p className="mb-6 text-sm text-gray-600">
              Este modelo não possui dados variáveis. Informe apenas a quantidade de cartões
              desejada para solicitar a impressão.
            </p>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Quantidade de cartões
                </label>
                <input
                  type="number"
                  min={1}
                  placeholder="Ex: 100"
                  className="h-11 w-full rounded-md border border-gray-300 px-3 outline-none focus:ring-2 focus:ring-blue-500"
                  onChange={(e) => setQtdModelosProvisirio(Number(e.target.value))}
                />
              </div>

              <div className="w-full rounded p-4 shadow">
                <Button
                  size="default"
                  variant="default"
                  className="h-8 w-full gap-1 text-neutral-50"
                  onClick={handleConfirmarSolicitacao}
                >
                  <span className="sm:whitespace-nowra sr-only text-neutral-50 sm:not-sr-only">
                    Solicitar Impressão da remessa
                  </span>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {isOpenModal && (
        <TermModal
          handleSubmitFile={handleSubmitFile}
          handleCloseModal={() => setIsOpenModal(false)}
        />
      )}

      {isModalSolicitacaoRemessa && (
        <ModalConfirmacaoRemessa
          isPending={isLoadingSolicitarRemessa}
          onCancel={() => setisModalSolicitacaoRemessa(false)}
          confirmarSolicitacao={handleSolicitarRemessa}
        />
      )}

      <TutorialVideoModal
        open={isTutorialModalOpen}
        onClose={() => setIsTutorialModalOpen(false)}
        videoUrl={videoUrl}
        videoNome={videoNome}
        isLoading={isLoadingVideoTutorial}
        isError={isErrorVideoTutorial}
      />
    </div>
  );
};

/**
 * EXPORTS
 */
export { DetailsModelPage };
